import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { LogoAvatar } from "@/components/LogoUploader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, MapPin, Phone, Mail, Search, ChevronLeft, ChevronRight,
  Power, PowerOff, Trash2, AlertTriangle, Loader2, Eye, Sparkles,
} from "lucide-react";
import EntityDetailDialog from "@/components/EntityDetailDialog";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_OPTIONS = [
  { value: "all",       label: "All" },
  { value: "approved",  label: "Approved" },
  { value: "rejected",  label: "Rejected" },
];
const PAGE_SIZE = 12;

export default function AdminSchoolsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("schools");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailKind, setDetailKind] = useState("school");
  const [detailId, setDetailId] = useState(null);

  const openDetail = (kind, id) => { setDetailKind(kind); setDetailId(id); setDetailOpen(true); };
  const [purgeOpen, setPurgeOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedQ, statusFilter, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: PAGE_SIZE,
        status_filter: statusFilter,
        ...(debouncedQ ? { q: debouncedQ } : {}),
      };
      const { data: res } = await api.get(`/admin/${tab}`, { params });
      setData(res || { items: [], total: 0, total_pages: 1 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [tab, page, debouncedQ, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const goto = (p) => {
    if (p < 1 || p > (data.total_pages || 1)) return;
    setPage(p);
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Schools &amp; Branches</h1>
          <p className="text-brand-inkMuted mt-1">Every school and branch on SchoolBoss.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {user?.role === "super_admin" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPurgeOpen(true)}
              className="rounded-full h-11 px-4 border-[#EF4444]/30 text-[#B91C1C] hover:bg-[#FEF2F2]"
              data-testid="purge-test-seed-btn"
              title="Remove TEST_* / test_*@t.com automated test rows. Real data is preserved."
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Purge test seed
            </Button>
          )}
          <div className="relative flex-1 sm:flex-none sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tab === "schools" ? "Search by school name or contact" : "Search by branch, school, or address"}
              className="rounded-2xl h-11 pl-10 border-[#2563EB]/20"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-11 px-4 rounded-2xl border border-[#2563EB]/20 bg-white text-sm font-heading text-foreground"
            data-testid="admin-status-filter"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-[#DBEAFE] rounded-full p-1 h-auto">
          <TabsTrigger value="schools" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8]" data-testid="tab-schools">
            Schools{tab === "schools" ? ` (${data.total})` : ""}
          </TabsTrigger>
          <TabsTrigger value="branches" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8]" data-testid="tab-branches">
            Branches{tab === "branches" ? ` (${data.total})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schools" className="mt-6">
          {renderGrid({ tab: "schools", data, loading, onChanged: load, openDetail })}
          {renderPagination({ page, total_pages: data.total_pages, total: data.total, goto })}
        </TabsContent>

        <TabsContent value="branches" className="mt-6">
          {renderGrid({ tab: "branches", data, loading, onChanged: load, openDetail })}
          {renderPagination({ page, total_pages: data.total_pages, total: data.total, goto })}
        </TabsContent>
      </Tabs>

      <EntityDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        kind={detailKind}
        id={detailId}
        role={user?.role}
        onChanged={load}
      />

      <PurgeTestSeedDialog
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        onPurged={load}
      />
    </>
  );
}

function renderGrid({ tab, data, loading, onChanged, openDetail }) {
  if (loading) {
    return <div className="text-brand-inkMuted py-12 text-center font-heading">Loading...</div>;
  }
  if (!data.items.length) {
    return (
      <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center text-brand-inkMuted" data-testid={`no-${tab}`}>
        No {tab} match your filters.
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`${tab}-grid`}>
      {data.items.map((it) =>
        tab === "schools"
          ? <SchoolCard key={it.id} s={it} onChanged={onChanged} onView={() => openDetail?.("school", it.id)} />
          : <BranchCard key={it.id} b={it} onChanged={onChanged} onView={() => openDetail?.("branch", it.id)} />
      )}
    </div>
  );
}

function SchoolCard({ s, onChanged, onView }) {
  const isActive = s.is_active !== false; // default true
  return (
    <div
      className={`bg-white rounded-3xl border border-[#2563EB]/10 shadow-sm p-5 hover:-translate-y-1 hover:shadow-md transition-all ${
        isActive ? "" : "opacity-60"
      }`}
      data-testid={`school-tile-${s.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <LogoAvatar logo={s.logo_base64} size="md" />
        <div className="flex items-center gap-1.5">
          {!isActive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-heading font-bold bg-slate-200 text-slate-600 uppercase">
              Deactivated
            </span>
          )}
          <StatusBadge status={s.status} />
        </div>
      </div>
      <h3 className="font-heading text-lg font-semibold text-foreground">{s.name}</h3>
      <p className="text-sm text-brand-inkMuted flex items-center gap-1.5 mt-1"><Mail className="w-3.5 h-3.5" />{s.contact_email}</p>
      <p className="text-sm text-brand-inkMuted flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{s.contact_phone}</p>
      {s.rejection_reason && s.status === "rejected" && (
        <div className="mt-3 rounded-xl bg-[#FFEBEE] text-[#C62828] px-3 py-2 text-xs">{s.rejection_reason}</div>
      )}

      {/* Super-admin actions */}
      {s.status === "approved" && (
        <ActionRow
          entity={s} kind="school" isActive={isActive} onChanged={onChanged}
          onView={onView}
          testidPrefix={`school-${s.id}`}
        />
      )}
      {s.status !== "approved" && onView && (
        <div className="mt-3">
          <Button size="sm" variant="outline" onClick={onView} className="rounded-full" data-testid={`school-${s.id}-view`}>
            <Eye className="w-3.5 h-3.5 mr-1" /> View
          </Button>
        </div>
      )}
    </div>
  );
}

function BranchCard({ b, onChanged, onView }) {
  const isActive = b.is_active !== false;
  return (
    <div
      className={`bg-white rounded-3xl border border-[#2563EB]/10 shadow-sm p-5 hover:-translate-y-1 hover:shadow-md transition-all ${
        isActive ? "" : "opacity-60"
      }`}
      data-testid={`branch-tile-${b.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <LogoAvatar
          logo={b.logo_base64}
          size="md"
          fallbackClass="bg-[#FFF3E0] text-[#E65100]"
        />
        <div className="flex items-center gap-1.5">
          {!isActive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-heading font-bold bg-slate-200 text-slate-600 uppercase">
              Deactivated
            </span>
          )}
          <StatusBadge status={b.status} />
        </div>
      </div>
      <h3 className="font-heading text-lg font-semibold text-foreground">{b.name}</h3>
      <p className="text-xs text-brand-inkMuted">of {b.school_name}</p>
      <p className="text-sm text-brand-inkMuted mt-2 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5" />{b.address}</p>
      <p className="text-sm text-brand-inkMuted flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{b.contact_phone}</p>
      {b.rejection_reason && b.status === "rejected" && (
        <div className="mt-3 rounded-xl bg-[#FFEBEE] text-[#C62828] px-3 py-2 text-xs">{b.rejection_reason}</div>
      )}

      {b.status === "approved" && (
        <ActionRow
          entity={b} kind="branch" isActive={isActive} onChanged={onChanged}
          onView={onView}
          testidPrefix={`branch-${b.id}`}
        />
      )}
      {b.status !== "approved" && onView && (
        <div className="mt-3">
          <Button size="sm" variant="outline" onClick={onView} className="rounded-full" data-testid={`branch-${b.id}-view`}>
            <Eye className="w-3.5 h-3.5 mr-1" /> View
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action row used on both School and Branch cards
// ---------------------------------------------------------------------------
function ActionRow({ entity, kind, isActive, onChanged, onView, testidPrefix }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const basePath = kind === "school" ? "/admin/schools" : "/admin/branches";

  const toggle = async () => {
    setBusy(true);
    try {
      const action = isActive ? "deactivate" : "activate";
      await api.post(`${basePath}/${entity.id}/${action}`);
      toast.success(`${kind === "school" ? "School" : "Branch"} ${action}d.`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
        {onView && (
          <Button
            variant="outline" size="sm"
            onClick={onView}
            className="rounded-full h-8 px-3 text-xs border-[#BFDBFE] text-[#1D4ED8] hover:bg-[#EFF6FF]"
            data-testid={`${testidPrefix}-view`}
          >
            <Eye className="w-3.5 h-3.5 mr-1" /> View
          </Button>
        )}
        <Button
          variant="outline" size="sm"
          onClick={toggle} disabled={busy}
          className={`rounded-full h-8 px-3 text-xs ${
            isActive
              ? "border-[#FED7AA] text-[#9A3412] hover:bg-[#FFF7ED]"
              : "border-[#BBF7D0] text-[#15803D] hover:bg-[#F0FDF4]"
          }`}
          data-testid={`${testidPrefix}-toggle`}
        >
          {isActive
            ? <><PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate</>
            : <><Power className="w-3.5 h-3.5 mr-1" /> Activate</>}
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => setConfirmOpen(true)}
          className="rounded-full h-8 px-3 text-xs border-[#FECACA] text-[#B91C1C] hover:bg-[#FEF2F2]"
          data-testid={`${testidPrefix}-delete`}
        >
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
      </div>
      <DeleteConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        entity={entity}
        kind={kind}
        basePath={basePath}
        onChanged={onChanged}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Two-step delete confirmation dialog.
// Step 1: warning + understood checkbox.
// Step 2: type the exact entity name to enable the Delete button.
// ---------------------------------------------------------------------------
function DeleteConfirmDialog({ open, onOpenChange, entity, kind, basePath, onChanged }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const expected = (entity?.name || "").trim();
  const matches = typed.trim() === expected && acknowledged;

  const close = () => {
    setAcknowledged(false); setTyped(""); setBusy(false);
    onOpenChange(false);
  };

  const doDelete = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`${basePath}/${entity.id}`, {
        data: { confirm_name: typed.trim() },
      });
      const d = data?.deleted || {};
      const parts = [
        d.branches != null ? `${d.branches} branch${d.branches === 1 ? "" : "es"}` : null,
        d.students != null ? `${d.students} student${d.students === 1 ? "" : "s"}` : null,
        d.employees != null ? `${d.employees} employee${d.employees === 1 ? "" : "s"}` : null,
      ].filter(Boolean);
      toast.success(
        `${kind === "school" ? "School" : "Branch"} "${expected}" deleted` +
          (parts.length ? ` · ${parts.join(", ")} wiped` : "")
      );
      close();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 shadow-floating" data-testid={`delete-dialog-${kind}-${entity?.id}`}>
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#FEE2E2] flex items-center justify-center mb-2">
            <AlertTriangle className="w-7 h-7 text-[#B91C1C]" />
          </div>
          <DialogTitle className="text-center font-heading text-xl font-bold">
            Permanently delete this {kind}?
          </DialogTitle>
          <DialogDescription className="text-center text-slate-500">
            You're about to delete <span className="font-semibold text-[#B91C1C]">"{expected}"</span>. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#7F1D1D] leading-relaxed">
            <p className="font-heading font-bold flex items-center gap-1.5 mb-1.5">
              <AlertTriangle className="w-4 h-4" /> The following will be permanently wiped:
            </p>
            <ul className="list-disc pl-5 space-y-0.5">
              {kind === "school" && <li>All branches under this school</li>}
              <li>All students, employees, classes and sections</li>
              <li>All attendance, grades, feedback and report cards</li>
              <li>All financial entries — tuition payments, income, expenses, payslips</li>
              {kind === "school" && <li>The owner account and every staff/parent user under it</li>}
            </ul>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 w-4 h-4 rounded text-[#B91C1C]" data-testid="delete-ack" />
            <span className="text-sm text-foreground">
              I understand this is permanent and will erase all related data.
            </span>
          </label>

          <div>
            <label className="text-xs font-heading uppercase tracking-wider text-slate-500">
              To confirm, type <span className="font-mono text-[#B91C1C]">{expected}</span> below
            </label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)}
              className="mt-1.5 rounded-2xl h-11 border-slate-200" placeholder={expected}
              data-testid="delete-confirm-input" disabled={!acknowledged} />
          </div>
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={close} className="rounded-full" data-testid="delete-cancel">
            Cancel
          </Button>
          <Button onClick={doDelete} disabled={!matches || busy}
            className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-50"
            data-testid="delete-confirm-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4 mr-1.5" /> Delete permanently</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function renderPagination({ page, total_pages, total, goto }) {
  if (!total) return null;
  return (
    <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="pagination-bar">
      <p className="text-sm text-brand-inkMuted">
        Showing <span className="font-heading font-semibold text-foreground">{Math.min(total, (page - 1) * 12 + 1)}–{Math.min(total, page * 12)}</span> of <span className="font-heading font-semibold text-foreground">{total}</span>
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="sm"
          onClick={() => goto(page - 1)}
          disabled={page <= 1}
          className="rounded-full border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-40"
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        <span className="px-3 py-1 text-sm font-heading text-brand-inkMuted">
          Page <span className="text-foreground font-semibold">{page}</span> of <span className="text-foreground font-semibold">{total_pages}</span>
        </span>
        <Button
          variant="outline" size="sm"
          onClick={() => goto(page + 1)}
          disabled={page >= total_pages}
          className="rounded-full border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-40"
          data-testid="pagination-next"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


function PurgeTestSeedDialog({ open, onClose, onPurged }) {
  const [confirmText, setConfirmText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    if (!open) { setConfirmText(""); setPreview(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/admin/cleanup-test-seed", {
          confirm: "WIPE-SEEDED-DATA",
          dry_run: true,
        });
        if (!cancelled) setPreview(data?.would_delete || null);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const canPurge =
    confirmText.trim() === "DELETE TEST DATA"
    && !!preview
    && (preview.test_users + preview.test_schools + preview.test_branches + preview.test_plans) > 0;

  const doPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-seed", {
        confirm: "WIPE-SEEDED-DATA",
        dry_run: false,
      });
      const tl = data?.deleted_top_level || {};
      const cascade = data?.total_cascade_rows ?? 0;
      toast.success(
        `Purged ${tl.test_schools || 0} schools, ${tl.test_branches || 0} branches, `
        + `${tl.test_users || 0} users, ${tl.test_plans || 0} plans, +${cascade} cascade rows.`
      );
      onClose?.();
      onPurged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPurging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !purging && onClose?.()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="purge-test-seed-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#B91C1C]" /> Purge test seed data
          </DialogTitle>
          <DialogDescription className="text-brand-inkMuted">
            Removes only rows created by automated tests (names starting with <code className="px-1 rounded bg-slate-100">TEST_</code> or emails matching <code className="px-1 rounded bg-slate-100">test_*@t.com</code>). Real data is preserved.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 flex items-center justify-center text-brand-inkMuted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Scanning…
          </div>
        ) : preview ? (
          <div className="rounded-2xl bg-[#FEF2F2] border border-[#FCA5A5] p-4 text-sm" data-testid="purge-preview">
            <div className="font-heading font-semibold text-[#B91C1C] mb-2">Will delete:</div>
            <ul className="space-y-1 text-[#7F1D1D]">
              <li>· {preview.test_schools} test schools</li>
              <li>· {preview.test_branches} test branches</li>
              <li>· {preview.test_users} test users</li>
              <li>· {preview.test_plans} test subscription plans</li>
              <li>· …plus every student / employee / payment / log scoped to them.</li>
            </ul>
            {preview.test_schools + preview.test_branches + preview.test_users + preview.test_plans === 0 && (
              <p className="mt-3 text-[#7F1D1D] font-medium">Nothing to purge — your data is already clean.</p>
            )}
          </div>
        ) : null}

        <div>
          <label className="text-sm font-heading text-foreground mb-1.5 block">
            Type <span className="font-mono font-bold">DELETE TEST DATA</span> to confirm:
          </label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE TEST DATA"
            disabled={purging}
            className="rounded-2xl h-11 border-[#FCA5A5] focus-visible:ring-[#B91C1C]/30"
            data-testid="purge-confirm-input"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            disabled={purging}
            className="rounded-full"
            data-testid="purge-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={doPurge}
            disabled={!canPurge || purging}
            className="rounded-full bg-[#B91C1C] hover:bg-[#7F1D1D] text-white disabled:opacity-50"
            data-testid="purge-confirm-btn"
          >
            {purging ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Purging…</> : <><Trash2 className="w-4 h-4 mr-1.5" /> Purge now</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
